import React, { useState, useEffect, useRef } from 'react';
import { Button, TextField, Tooltip, IconButton, Drawer } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import GameRulesDialog from '../game/GameRulesDialog';
import ServerStats from '../ui/ServerStats';
import HighScores from '../ui/HighScores';
import FeedbackForm from '../ui/FeedbackForm';
import { getCachedPlayerEmoji, cachePlayerName, cacheRoomName } from '../../utils/localStorageUtils';
import { useMobileDetection } from '../../utils/deviceDetection';


const LoginPage = ({ socket, setLoggedIn, name, setName, room, setRoom }) => {
    const [errorMessage, setErrorMessage] = useState('');
    const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const nameInputRef = useRef(null);
    const screenshotRef = useRef(null);
    const leaderboardRef = useRef(null);
    const isMobile = useMobileDetection();

    // Focus name field on mount
    useEffect(() => {
        if (nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, []);

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const roomParam = urlParams.get('room');
        if (roomParam) {
            const normalizedRoom = roomParam.toLowerCase();
            setRoom(normalizedRoom);
            // Cache room name from URL param
            cacheRoomName(normalizedRoom);
        }
    }, [setRoom]);

    useEffect(() => {
        const currentSocket = socket.current;
        if (!currentSocket) return;

        const handleJoinError = (error) => {
            setErrorMessage(error);
            // Clear error after 5 seconds
            setTimeout(() => setErrorMessage(''), 5000);
        };

        // Handle start_game event (for reconnecting players)
        const handleStartGame = () => {
            // If game starts while on login page, it means player is reconnecting
            // Set loggedIn to true to transition to GamePage
            setLoggedIn(true);
        };

        currentSocket.on('join_room_error', handleJoinError);
        currentSocket.on('start_game', handleStartGame);

        return () => {
            if (currentSocket) {
                currentSocket.off('join_room_error', handleJoinError);
                currentSocket.off('start_game', handleStartGame);
            }
        };
    }, [socket, setLoggedIn]);

    const nameChangeHandler = (e) => {
        const newName = e.target.value;
        setName(newName);
        setErrorMessage(''); // Clear error when user types
        
        // Cache name to localStorage
        cachePlayerName(newName);
    };
    const roomChangeHandler = (e) => {
        const newRoom = e.target.value.toLowerCase();
        setRoom(newRoom);
        setErrorMessage(''); // Clear error when user types
        
        // Cache room name to localStorage
        cacheRoomName(newRoom);
    };

    // Generate a random room name (always lowercase)
    const generateRandomRoomName = () => {
        const adjectives = ['bull', 'bear', 'moon', 'diamond', 'golden', 'silver', 'crypto', 'mega', 'super', 'epic', 'wild', 'fast', 'lucky', 'rich'];
        const nouns = ['market', 'street', 'trade', 'fund', 'stock', 'coin', 'vault', 'bank', 'exchange', 'portfolio'];
        const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        const num = Math.floor(Math.random() * 1000);
        return `${adj}${noun}${num}`.toLowerCase();
    };

    const joinRoomHandler = () => {
        setErrorMessage(''); // Clear any previous errors

        if (!name.trim()) {
            setErrorMessage('Please enter your name');
            return;
        }
        if (!room.trim()) {
            setErrorMessage('Please enter a room ID');
            return;
        }
        
        // Check if socket is connected
        if (!socket.current || !socket.current.connected) {
            setErrorMessage('Cannot connect to server. Please wait and try again.');
            return;
        }
        
        // Only set loggedIn to true after server confirms join
        // Listen for player_count or player_names as confirmation
        let hasConfirmed = false;
        const handleJoinConfirmation = () => {
            if (!hasConfirmed) {
                hasConfirmed = true;
                setLoggedIn(true);
                socket.current.off('player_count', handleJoinConfirmation);
                socket.current.off('player_names', handleJoinConfirmation);
            }
        };
        
        // Set a timeout in case server doesn't respond
        const timeout = setTimeout(() => {
            if (!hasConfirmed) {
                socket.current.off('player_count', handleJoinConfirmation);
                socket.current.off('player_names', handleJoinConfirmation);
                setErrorMessage('Failed to join room. Please check your connection and try again.');
            }
        }, 5000);
        
        const handleConfirmationWithTimeout = () => {
            clearTimeout(timeout);
            handleJoinConfirmation();
        };
        
        // Get cached emoji from localStorage
        const cachedEmoji = getCachedPlayerEmoji();

        // Normalize room code to lowercase
        const normalizedRoom = room.toLowerCase();
        setRoom(normalizedRoom);
        
        // Cache room name when joining
        cacheRoomName(normalizedRoom);

        socket.current.once('player_count', handleConfirmationWithTimeout);
        socket.current.once('player_names', handleConfirmationWithTimeout);
        socket.current.emit('join_room', { room: normalizedRoom, name, preferredEmoji: cachedEmoji });
    };

    const quickPlayHandler = () => {
        setErrorMessage(''); // Clear any previous errors

        if (!name.trim()) {
            setErrorMessage('Please enter your name first');
            return;
        }
        
        // Check if socket is connected
        if (!socket.current || !socket.current.connected) {
            setErrorMessage('Cannot connect to server. Please wait and try again.');
            return;
        }

        // Generate random room name (already lowercase)
        const quickPlayRoom = generateRandomRoomName();
        setRoom(quickPlayRoom);
        
        // Join room and add bots after confirmation
        let hasConfirmed = false;
        const handleJoinConfirmation = () => {
            if (!hasConfirmed) {
                hasConfirmed = true;
                setLoggedIn(true);
                socket.current.off('player_count', handleJoinConfirmation);
                socket.current.off('player_names', handleJoinConfirmation);
                
                // Add 3 bots after joining
                setTimeout(() => {
                    socket.current.emit('add_bot', { room: quickPlayRoom });
                    setTimeout(() => {
                        socket.current.emit('add_bot', { room: quickPlayRoom });
                        setTimeout(() => {
                            socket.current.emit('add_bot', { room: quickPlayRoom });
                        }, 200);
                    }, 200);
                }, 300);
            }
        };
        
        const timeout = setTimeout(() => {
            if (!hasConfirmed) {
                socket.current.off('player_count', handleJoinConfirmation);
                socket.current.off('player_names', handleJoinConfirmation);
                setErrorMessage('Failed to start quick play. Please try again.');
            }
        }, 5000);
        
        const handleConfirmationWithTimeout = () => {
            clearTimeout(timeout);
            handleJoinConfirmation();
        };
        
        // Get cached emoji from localStorage
        const cachedEmoji = getCachedPlayerEmoji();

        // Cache room name when joining quick play
        cacheRoomName(quickPlayRoom);

        socket.current.once('player_count', handleConfirmationWithTimeout);
        socket.current.once('player_names', handleConfirmationWithTimeout);
        socket.current.emit('join_room', { room: quickPlayRoom, name, preferredEmoji: cachedEmoji });
    };

    const scrollToSection = (ref) => {
        if (ref.current) {
            ref.current.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'start' 
            });
        }
        setMobileMenuOpen(false);
    };

    const handleOpenRules = () => {
        setRulesDialogOpen(true);
        setMobileMenuOpen(false);
    };

    return (
        <div className="login-page">
            {/* Header */}
            <header className="landing-header">
                <div className="landing-header-content">
                    {isMobile && (
                        <IconButton
                            className="landing-hamburger"
                            onClick={() => setMobileMenuOpen(true)}
                            aria-label="Open menu"
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <div className="landing-logo">
                        <span className="logo-icon">📈</span>
                        <div className="logo-text-container">
                            <h1 className="logo-text">Pump N Dump</h1>
                            <p className="landing-subtitle">The Stock Market Strategy Card Game</p>
                        </div>
                    </div>
                    <nav className="landing-nav">
                        <button 
                            className="landing-nav-link"
                            onClick={() => scrollToSection(screenshotRef)}
                        >
                            Screenshots
                        </button>
                        <button 
                            className="landing-nav-link"
                            onClick={() => scrollToSection(leaderboardRef)}
                        >
                            Leaderboard
                        </button>
                        <button 
                            className="landing-nav-link"
                            onClick={() => setRulesDialogOpen(true)}
                        >
                            Game Manual
                        </button>
                        <button 
                            className="landing-nav-link"
                            onClick={() => setFeedbackDialogOpen(true)}
                        >
                            Contact Us
                        </button>
                    </nav>
                </div>
            </header>

            <div className="landing-container">
                {/* Hero Section: Login | Server Details */}
                <section className="landing-hero">
                    <div className="landing-hero-content">
                        <div className="landing-hero-left">
                            <div className="login-card">
                                <div className="login-header">
                                    <h2 className="login-card-title">Play Now!</h2>
                                    <div className="login-beta-warning">
                                        <p>
                                            ⚠️ <strong>Beta:</strong> This is an online proof of concept of a card game. The rules are still being tested and may change at any time. The server may also be rebooted at any time.
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="login-form">
                                    {errorMessage && (
                                        <div className="login-error-message">
                                            {errorMessage}
                                        </div>
                                    )}
                                    <div className="login-name-row">
                                        <TextField
                                            value={name}
                                            onChange={nameChangeHandler}
                                            className='login-textfield'
                                            variant='outlined'
                                            label='Your Name'
                                            placeholder='Enter your name'
                                            inputProps={{ maxLength: 25 }}
                                            inputRef={nameInputRef}
                                            style={{ flex: 1 }}
                                        />
                                        <Tooltip 
                                            title="Start a game instantly with 3 AI opponents" 
                                            arrow 
                                            placement="top"
                                        >
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                onClick={quickPlayHandler}
                                                className="quick-play-button"
                                            >
                                                🤖 Quick Play
                                            </Button>
                                        </Tooltip>
                                    </div>
                                    <TextField
                                        value={room}
                                        onChange={roomChangeHandler}
                                        className='login-textfield'
                                        variant='outlined'
                                        label='Room Code'
                                        placeholder='Enter or create room code'
                                        inputProps={{ maxLength: 25 }}
                                        fullWidth
                                    />
                                    <Button 
                                        color="primary" 
                                        variant="contained" 
                                        onClick={joinRoomHandler}
                                        className="join-button"
                                        size="large"
                                        fullWidth
                                    >
                                        Join / Create Game
                                    </Button>
                                </div>

                                <div className="login-footer">
                                    <p>💡 Create a new room or join an existing one</p>
                                </div>
                            </div>
                        </div>
                        <div className="landing-hero-right">
                            <div className="server-stats-card">
                                <h2 className="server-stats-card-title">Player Stats</h2>
                                <ServerStats socket={socket} connectionUrl={process.env.REACT_APP_SERVER_URL || "/"} />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Gameplay Screenshot Section */}
                <section className="landing-screenshot" ref={screenshotRef}>
                    <div className="landing-screenshot-card">
                        <h2 className="landing-screenshot-card-title">Play the Market... and Your Friends</h2>
                        <p className="landing-screenshot-summary">
                            Manipulate and trade stocks and your to your advantage and compete with your friends to build the highest net worth.
                        </p>
                        <div className="landing-screenshot-wrapper">
                            {isMobile ? (
                                <img 
                                    src="/gameplay-mobile.png" 
                                    alt="Mobile gameplay preview" 
                                    className="landing-screenshot-image"
                                />
                            ) : (
                                <img 
                                    src="/gameplay-desktop.png" 
                                    alt="Desktop gameplay preview" 
                                    className="landing-screenshot-image"
                                />
                            )}
                        </div>
                    </div>
                </section>

                {/* Leaderboard Section */}
                <section className="landing-leaderboard" ref={leaderboardRef}>
                    <div className="landing-leaderboard-content">
                        <HighScores connectionUrl={process.env.REACT_APP_SERVER_URL || "/"} />
                    </div>
                </section>
            </div>

            {/* Game Manual Dialog */}
            <GameRulesDialog
                open={rulesDialogOpen}
                onClose={() => setRulesDialogOpen(false)}
            />

            {/* Feedback Form Dialog */}
            <FeedbackForm
                open={feedbackDialogOpen}
                onClose={() => setFeedbackDialogOpen(false)}
            />

            {/* Mobile Menu Drawer */}
            {isMobile && (
                <Drawer
                    anchor="left"
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    className="landing-mobile-drawer"
                    PaperProps={{ className: "landing-mobile-drawer-paper" }}
                >
                    <div className="landing-mobile-drawer-header">
                        <h2 className="landing-mobile-drawer-title">Menu</h2>
                        <IconButton
                            onClick={() => setMobileMenuOpen(false)}
                            className="landing-mobile-drawer-close"
                            aria-label="Close menu"
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className="landing-mobile-drawer-content">
                        <button 
                            className="landing-mobile-nav-link"
                            onClick={() => scrollToSection(screenshotRef)}
                        >
                            Screenshots
                        </button>
                        <button 
                            className="landing-mobile-nav-link"
                            onClick={() => scrollToSection(leaderboardRef)}
                        >
                            Leaderboard
                        </button>
                        <button 
                            className="landing-mobile-nav-link"
                            onClick={handleOpenRules}
                        >
                            Game Manual
                        </button>
                        <button 
                            className="landing-mobile-nav-link"
                            onClick={() => {
                                setFeedbackDialogOpen(true);
                                setMobileMenuOpen(false);
                            }}
                        >
                            Contact Us
                        </button>
                    </div>
                </Drawer>
            )}
        </div>
    )
};

export default LoginPage;

